<template>
  <div class="flex flex-row justify-between items-center px-2 pb-3 w-full bg-white" v-if="isMobile">
    <div class="flex items-center">
      <StyledTab :active="currentTab === 'edit'" @click="toggleTab('edit')">
        Edit listing
      </StyledTab>
      <StyledTab :active="currentTab === 'preview'" @click="toggleTab('preview')">
        Preview handout
      </StyledTab>
    </div>
  </div>
  <div class="md:max-w-screen-xl flex flex-col relative min-h-screen">
    <div class="hidden md:flex flex-row pb-5">
      <div class="flex flex-row cursor-pointer" @click="backToDashboard">
        <div class="flex items-center">
          <ChevronLeftIcon class="text-brand-green w-5 h-5" />
        </div>
        <div class="flex items-center">
          <p class="text-brand-dark-gray">Back to forms</p>
        </div>
      </div>
    </div>

    <div class="flex flex-col md:flex-row">
      <div
        id="flyer-preview"
        class="rounded-lg bg-white px-5 py-5 flex flex-col md:w-1/2 my-5 md:my-0 md:mr-10 lg:w-1/3"
        v-show="!isMobile || (isMobile && currentTab === 'preview')"
      >
        <template v-if="getCurrentUser.landlord.logo">
          <div class="flex items-center" style="max-width: 200px">
            <img
              id="company-logo"
              :src="getCurrentUser.landlord.logo"
              class=""
              style="max-width: 100%; max-height: 100%"
            />
          </div>

          <Line />
        </template>

        <h5>Listed by:</h5>
        <div class="flex item-center py-2">
          <div class="flex items-center w-1/3 mr-3">
            <template v-if="getCurrentUser.landlord.profile_photo">
              <div class="max-h-24">
                <img
                  class="h-24 w-24 rounded-full"
                  id="agent-photo"
                  :src="getCurrentUser.landlord.profile_photo"
                />
              </div>
            </template>
            <template v-else>
              <img
                class="h-24 w-24 rounded-full"
                id="agent-photo"
                crossorigin="anonymous"
                src="@/assets/images/default-profile.png"
              />
            </template>
          </div>
          <div class="flex flex-col w-2/3">
            <h5>{{ getCurrentUser.landlord.name }}</h5>
            <p v-if="getCurrentUser.landlord.company_name">
              {{ getCurrentUser.landlord.company_name }}
            </p>
            <p>{{ getCurrentUser.landlord.contact_email }}</p>
            <p>{{ getCurrentUser.landlord.phone }}</p>
            <p>{{ getCurrentUser.landlord.address }}</p>
          </div>
        </div>

        <Line />

        <h5>Listing:</h5>

        <div class="flex flex-col py-2">
          <p>{{ street || "123 Testing Rd." }} {{ unit || "" }}</p>
          <p>{{ city || "Testing City" }}, {{ state || "CA" }} {{ zip || "00000" }}</p>
          <div class="flex justify-between py-2 w-2/3">
            <div class="flex flex-col">
              <p class="text-brand-mid-gray text-sm">Rent</p>
              <p>{{ rent ? `${formatCurrency(rent)}` : "N/A" }}</p>
            </div>

            <div class="flex flex-col">
              <p class="text-brand-mid-gray text-sm">Available on</p>
              <p>{{ availableDate || "N/A" }}</p>
            </div>
          </div>
        </div>

        <Line />
        <div class="flex items-center py-2">
          <div class="flex">
            <img v-if="!generatedQRCode" src="@/assets/images/qr.png" class="w-full" />

            <canvas :class="[generatedQRCode ? '' : 'hidden']" id="qr-canvas"></canvas>
          </div>
          <div class="flex flex-col pl-2 overflow-hidden">
            <h5 class="">Apply for this property</h5>
            <p class="text-sm">To apply, visit</p>
            <p class="text-brand-green break-all text-sm">
              {{
                selectedForm
                  ? `rentapplication.net/apply/${selectedForm.slug}`
                  : "Please select a form."
              }}
            </p>

            <p class="text-sm">Or scan the QR code to open the app.</p>
            <div class="flex items-center my-2">
              <img src="@/assets/images/main-logo.svg" class="w-6 h-6 mr-1" id="logo-image" />
              <p class="text-xs italic text-brand-mid-gray whitespace-nowrap">
                powered by Rent Application
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        class="rounded-lg bg-white px-5 py-5 flex flex-col my-5 md:my-0 md:w-1/2 lg:w-2/3"
        v-show="!isMobile || (isMobile && currentTab === 'edit')"
      >
        <h4 class="py-2">Download Printable Promotional Listing Handouts</h4>

        <p class="py-1">
          Download and print assets for your listings. Applicants will be able to scan the QR code
          to directly apply using your personalized form.
        </p>
        <div class="flex items-center">
          <router-link class="text-brand-green py-5" :to="{ name: 'dashboard_settings_profile' }">
            Update personal details
          </router-link>
        </div>

        <div class="flex flex-col py-3">
          <label for="input" class="block py-1">
            Select form to use <span class="text-brand-red">*</span></label
          >

          <FormDropdown @select="formSelect" />
        </div>

        <div class="flex flex-col py-3">
          <label for="input" class="block"> Listing Address </label>

          <div class="flex flex-row justify-between">
            <div class="flex items-center w-2/3">
              <SingleLineInput
                @input="street = $event.target.value"
                placeholder="Street Address"
                :value="street"
              />
            </div>
            <div class="flex items-center w-1/4">
              <SingleLineInput
                @input="unit = $event.target.value"
                placeholder="Unit #"
                :value="unit"
              />
            </div>
          </div>

          <div class="flex flex-row justify-between">
            <div class="flex items-center w-1/3">
              <SingleLineInput
                @input="city = $event.target.value"
                placeholder="City"
                :value="city"
              />
            </div>
            <div class="flex items-center w-1/4">
              <DropDown
                :options="stateOptions"
                placeholder="State"
                @select="state = $event.value"
                ref="state"
              ></DropDown>
            </div>
            <div class="flex items-center w-1/3">
              <SingleLineInput
                placeholder="Zip"
                @input="zip = $event.target.value"
                :value="zip"
                mask="zipcode"
              />
            </div>
          </div>
        </div>

        <div class="flex flex-row py-3">
          <div class="flex items-center mr-5">
            <div class="flex flex-col">
              <label>Proposed Rent</label>
              <SingleLineInput
                @input="rent = $event.target.value"
                placeholder="Enter rent"
                :value="rent"
                mask="number"
              />
            </div>
          </div>

          <div class="flex items-center">
            <div class="flex flex-col">
              <label>Available on date</label>
              <SingleLineInput
                @input="availableDate = $event.target.value"
                placeholder="01/01/2020"
                :value="availableDate"
                mask="date"
              />
            </div>
          </div>
        </div>

        <div class="flex flex-col justify-end py-5">
          <div v-if="errorMessage" class="flex items-center">
            <p class="text-brand-red">{{ errorMessage }}</p>
          </div>
          <div class="flex w-full md:w-1/2 md:mx-auto justify-end md:justify-center">
            <div class="flex items-center mr-10">
              <p class="cursor-pointer" @click="backToDashboard()">Cancel</p>
            </div>

            <div class="flex items-center">
              <PrimaryButton
                @click="generatePDF"
                :is-loading="isGeneratingPDF"
                :disabled="isGeneratingPDF"
                >Download Assets</PrimaryButton
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { ChevronLeftIcon } from "@heroicons/vue/outline"

import { mapGetters } from "vuex"

import stateOptions from "@/constants/states"

import FormDropdown from "./components/FormDropdown.vue"

import QRCode from "qrcode"
// import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import { saveAs } from "file-saver"
import axios from "axios"

export default {
  components: {
    ChevronLeftIcon,
    FormDropdown
  },
  computed: {
    ...mapGetters(["getCurrentUser", "getApplicationTemplates"])
  },
  data() {
    return {
      stateOptions,
      selectedForm: null,
      street: "",
      unit: "",
      city: "",
      state: "",
      zip: "",
      rent: "",
      availableDate: "",
      apptemplateOptions: null,
      generatedQRCode: false,
      errorMessage: "",
      isGeneratingPDF: false,
      currentTab: "edit"
    }
  },
  watch: {
    selectedForm: {
      handler(val) {
        if (val) {
          this.generateQRCode()
          this.errorMessage = ""
        }
      },
      immediate: true,
      deep: true
    },
    street: {
      handler(val) {
        if (val) {
          this.generateQRCode()
          this.errorMessage = ""
        }
      },
      immediate: true
    },
    unit: {
      handler(val) {
        if (val) {
          this.generateQRCode()
          this.errorMessage = ""
        }
      },
      immediate: true
    },
    city: {
      handler(val) {
        if (val) {
          this.generateQRCode()
          this.errorMessage = ""
        }
      },
      immediate: true
    },
    state: {
      handler(val) {
        if (val) {
          this.generateQRCode()
          this.errorMessage = ""
        }
      },
      immediate: true
    },
    zip: {
      handler(val) {
        if (val) {
          this.generateQRCode()
          this.errorMessage = ""
        }
      },
      immediate: true
    }
  },
  async mounted() {},
  methods: {
    formatCurrency(number) {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(number)
    },
    toggleTab(tab) {
      this.currentTab = tab
    },
    backToDashboard() {
      this.$router.go(-1)
    },
    formSelect(val) {
      this.selectedForm = val
    },
    async generateQRCode() {
      const that = this
      const canvas = document.getElementById("qr-canvas")

      let propertyEncoded

      if (this.street) {
        const { street, unit, city, state, zip } = this
        propertyEncoded = btoa(
          JSON.stringify({
            street,
            unit,
            city,
            state,
            zip
          })
        )
      }

      const url = `${this.baseHostName}/${this.selectedForm.slug}?a=${btoa(
        this.getCurrentUser.username
      )}${propertyEncoded ? `&p=${propertyEncoded}` : ""}`

      await QRCode.toCanvas(canvas, url, { scale: 3 }, (error) => {
        if (error) {
          console.error(error)
        } else {
          const ctx = canvas.getContext("2d")

          const logoImage = new Image()
          logoImage.src =
            "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjUiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NSA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3QgeD0iMC4wMDM5MDYyNSIgeT0iMC4wMDA5NzY1NjIiIHdpZHRoPSI2NCIgaGVpZ2h0PSI2NCIgcng9IjMyIiBmaWxsPSIjMzY1OUMwIi8+CjxjaXJjbGUgY3g9IjMyLjAwMzkiIGN5PSIzMS45OTk2IiByPSIyOSIgZmlsbD0iIzMzMzMzMyIvPgo8cGF0aCBkPSJNMTUgMzIuMDAwMkgxNy42OEMxNy44NTY3IDMyLjAwMDIgMTggMzIuMTQzNSAxOCAzMi4zMjAyVjQzLjAwMDJINDZWMzIuMzIwMkM0NiAzMi4xNDM1IDQ2LjE0MzMgMzIuMDAwMiA0Ni4zMiAzMi4wMDAySDQ5QzQ5LjU1MjMgMzIuMDAwMiA1MCAzMS41NTI1IDUwIDMxLjAwMDJDNTAgMzAuNDQ3OSA0OS41NTIzIDMwLjAwMDIgNDkgMzAuMDAwMkg0OEw0NiAyOC41MDAyVjI0LjAwMDJDNDYgMjMuNDQ3OSA0NS41NTIzIDIzLjAwMDIgNDUgMjMuMDAwMkg0M0M0Mi40NDc3IDIzLjAwMDIgNDIgMjMuNDQ3OSA0MiAyNC4wMDAyVjI0Ljg1NzNMMzUuMTIzNSAxOC45NjMyQzM0LjM5ODUgMTguMzQxOCAzMy40NzUxIDE4LjAwMDIgMzIuNTIwMyAxOC4wMDAySDMxLjQ3OTdDMzAuNTI0OCAxOC4wMDAyIDI5LjYwMTUgMTguMzQxOCAyOC44NzY1IDE4Ljk2MzJMMTYgMzAuMDAwMkgxNUMxNC40NDc3IDMwLjAwMDIgMTQgMzAuNDQ3OSAxNCAzMS4wMDAyQzE0IDMxLjU1MjUgMTQuNDQ3NyAzMi4wMDAyIDE1IDMyLjAwMDJaIiBmaWxsPSJ3aGl0ZSIvPgo8bWFzayBpZD0ibWFzazAiIHN0eWxlPSJtYXNrLXR5cGU6YWxwaGEiIG1hc2tVbml0cz0idXNlclNwYWNlT25Vc2UiIHg9IjMiIHk9IjIiIHdpZHRoPSI1OSIgaGVpZ2h0PSI1OSI+CjxjaXJjbGUgY3g9IjMyLjAwMzkiIGN5PSIzMS4wMDAzIiByPSIyOSIgZmlsbD0iI0M0QzRDNCIvPgo8L21hc2s+CjxnIG1hc2s9InVybCgjbWFzazApIj4KPGNpcmNsZSBjeD0iMzIuMDAzOSIgY3k9Ijg5LjAwMDYiIHI9IjQ4IiBmaWxsPSIjMTZBMDg1IiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjIiLz4KPC9nPgo8Y2lyY2xlIGN4PSIzMi4wMDM5IiBjeT0iMzEuOTk5NiIgcj0iMjgiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMiIvPgo8Y2lyY2xlIGN4PSIzMi4wMDM5IiBjeT0iMzEuOTk5NiIgcj0iMzAiIHN0cm9rZT0iIzM2NTlDMCIgc3Ryb2tlLXdpZHRoPSIyIi8+CjxyZWN0IHg9IjI4LjAwMzkiIHk9IjMxLjAwMDYiIHdpZHRoPSI4IiBoZWlnaHQ9IjgiIHJ4PSIxIiBmaWxsPSIjMzMzMzMzIi8+CjxyZWN0IHg9IjI4LjAwMzkiIHk9IjM0LjAwMDciIHdpZHRoPSI4IiBoZWlnaHQ9IjIiIGZpbGw9IndoaXRlIi8+CjxyZWN0IHg9IjMxLjAwMzkiIHk9IjM5LjAwMDgiIHdpZHRoPSI4IiBoZWlnaHQ9IjIiIHRyYW5zZm9ybT0icm90YXRlKC05MCAzMS4wMDM5IDM5LjAwMDgpIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K"
          logoImage.width = 20
          logoImage.height = 20
          logoImage.onload = function () {
            ctx.drawImage(logoImage, canvas.width / 2.5, canvas.height / 2.5, 25, 25)
            that.generatedQRCode = true
          }
        }
      })
    },
    async generatePDF() {
      if (!this.selectedForm || this.isGeneratingPDF) {
        this.errorMessage = "Form is required. Please select a form."
        return
      }

      this.isGeneratingPDF = true

      const that = this
      const previewCanvas = document.getElementById("flyer-preview")

      // Adjustments for flyer image
      if (that.isMobile) {
        document.getElementById("logo-image").style.display = "none"
      } else {
        document.getElementById("logo-image").style.marginTop = "15px"
      }

      if (this.getCurrentUser.landlord.logo) {
        //company-logo
        try {
          let response = await axios.get(
            `${this.getCurrentUser.landlord.logo}&not-from-cache-please`,
            {
              responseType: "arraybuffer",
              withCredentials: false,
              headers: { "Cache-Control": "no-cache" }
            }
          )

          let dataUrl = Buffer.from(response.data, "binary").toString("base64")

          document
            .getElementById("company-logo")
            .setAttribute("src", `data:image/png;base64, ${dataUrl}`)
        } catch (err) {
          console.log(err)
        }
      }

      if (this.getCurrentUser.landlord.profile_photo) {
        try {
          let response = await axios.get(
            `${this.getCurrentUser.landlord.profile_photo}&not-from-cache-please`,
            {
              responseType: "arraybuffer",
              withCredentials: false,
              headers: { "Cache-Control": "no-cache" }
            }
          )

          let dataUrl = Buffer.from(response.data, "binary").toString("base64")
          document
            .getElementById("agent-photo")
            .setAttribute("src", `data:image/png;base64, ${dataUrl}`)
        } catch (err) {
          console.log(err)
        }
      }

      // TODO: Find a fix for flashing
      if (that.isMobile) {
        previewCanvas.style.display = "flex"
        previewCanvas.style.zIndex = "-50"
      }

      html2canvas(previewCanvas, {
        useCORS: true,
        allowTaint: false,
        scale: 2,
        quality: 4,
        x: 0,
        y: 0,
        windowWidth: 450,
        windowHeight: 700,
        backgroundColor: "none"
      }).then((canvas) => {
        if (that.isMobile) {
          previewCanvas.style.display = "none"
          previewCanvas.style.zIndex = "0"
        }

        canvas.toBlob((blob) => {
          saveAs(blob, `flyer_${that.selectedForm.slug}.png`)
        })

        // Revert adjustment for flyer image
        if (that.isMobile) {
          document.getElementById("logo-image").style.display = "block"
        } else {
          document.getElementById("logo-image").style.marginTop = "0px"
        }

        this.isGeneratingPDF = false
      })
    }
  }
}
</script>
